import { ROUTES, categoryKeyFor } from "../shared/classData";

const DAY = 86400000;
const SOON_DAYS = 60;
const CAT_COLORS = {
  fire: "var(--color-cat-firefighter)",
  ems: "var(--color-cat-ems)",
  aha: "var(--color-cat-aha)",
  res: "var(--color-cat-rescue)",
};
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const longDate = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const clock = (d) => d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });

/** "Today" / "Tomorrow" / "In N days" for a future date. */
export function dayWord(at, now) {
  const diff = Math.round((startOfDay(new Date(at)) - startOfDay(now)) / DAY);
  return diff <= 0 ? "Today" : diff === 1 ? "Tomorrow" : `In ${diff} days`;
}


/**
 * Individual upcoming sessions for classes the student is ENROLLED in, from each class's `schedule`
 * (so a class already in progress still shows its remaining sessions). Falls back to the payload's
 * `upcomingClasses` (first-session dates only) when registrations carry no schedule.
 */
function upcomingSessions(dashboard, now) {
  const today = startOfDay(now);
  const fromSchedule = (dashboard.registrations || [])
    .filter((r) => r.enrollmentStatus === "ENROLLED" && r.class)
    .flatMap((r) => (Array.isArray(r.class.schedule) ? r.class.schedule : []).map((s) => ({
      classId: r.classId ?? r.class.id, title: r.class.title || r.classTitle || "Training session", category: r.class.category, start: s.start, location: s.location,
    })))
    .filter((u) => new Date(u.start) >= today);
  const list = fromSchedule.length ? fromSchedule : (dashboard.upcomingClasses || [])
    .filter((c) => c.date && new Date(c.date) >= today)
    .map((c) => ({ classId: c.classId || c.id, title: c.title || c.classTitle || "Training session", start: c.date, location: c.location }));
  return list.sort((a, b) => new Date(a.start) - new Date(b.start));
}

function buildCerts(rawCerts, now, tenantName) {
  return rawCerts.map((c) => {
    const exp = c.expires ? new Date(c.expires) : null;
    const days = exp ? Math.round((startOfDay(exp) - startOfDay(now)) / DAY) : null;
    const status = days == null ? "act" : days < 0 ? "exp" : days <= SOON_DAYS ? "soon" : "act";
    const org = c.issuer || c.issuedBy || c.issuingOrganization || tenantName;
    return {
      id: c.id ?? `${c.name}-${c.certNumber}`,
      name: c.name || "Certification",
      org,
      number: c.certNumber ? `#${c.certNumber}` : "",
      exp,
      days,
      status,
    };
  }).sort((a, b) => (a.exp ? +a.exp : Infinity) - (b.exp ? +b.exp : Infinity));
}

function buildAttention(certs, unread, go) {
  const items = [
    ...certs.filter((c) => c.status === "exp").map((c) => ({ id: `exp-${c.id}`, tone: "r", icon: "alert", title: `${c.name} expired`, detail: [longDate(c.exp), [c.org, c.number].filter(Boolean).join(" ")].join(" · "), cta: "Find renewal", onClick: () => go(ROUTES.certifications) })),
    ...certs.filter((c) => c.status === "soon").map((c) => ({ id: `soon-${c.id}`, tone: "w", icon: "cert", title: `${c.name} expires in ${c.days} day${c.days === 1 ? "" : "s"}`, detail: `Valid to ${longDate(c.exp)}`, cta: "Renew", onClick: () => go(ROUTES.certifications) })),
  ];
  if (unread > 0) items.push({ id: "unread", tone: "v", icon: "msg", title: `${unread} unread message${unread === 1 ? "" : "s"}`, detail: "From your training coordinator", cta: "Open", onClick: () => go(ROUTES.messaging) });
  return items;
}

/** Pure view-model builder for StudentDashboard. */
export function buildStudentModel({ loading = false, dashboard = {}, unread = 0, user, tenantConfig, navigate = () => {}, now = new Date(), showTrack = false }) {
  const tenantName = tenantConfig?.name || "Training Center";
  const metrics = dashboard.metrics || {};
  const certs = buildCerts(dashboard.certifications || dashboard.userCerts || [], now, tenantName);
  const expired = certs.filter((c) => c.status === "exp").length;
  const upcoming = upcomingSessions(dashboard, now).slice(0, 5).map((u) => {
    const at = new Date(u.start);
    return {
      id: `${u.classId ?? u.title}-${u.start}`,
      month: at.toLocaleDateString("en-US", { month: "short" }),
      day: String(at.getDate()).padStart(2, "0"),
      time: clock(at),
      color: CAT_COLORS[categoryKeyFor(u.category, u.title)],
      title: u.title,
      place: u.location || "",
      when: dayWord(at, now),
      at,
      onClick: u.classId ? () => navigate(ROUTES.studentHub(u.classId)) : undefined,
    };
  });
  const earned = certs.length - expired;
  const required = num(metrics.certificationsRequired);
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const rangeEnd = new Date(now.getFullYear() + 1, 1, 1);
  const first = (user?.fullName || user?.name || user?.username?.split("@")[0] || "there").split(" ")[0];
  const attention = buildAttention(certs, unread, navigate);
  return {
    loading,
    tenantName,
    header: {
      eyebrow: `${tenantName} · ${now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`,
      greeting: `Welcome back, ${first}`,
    },
    next: upcoming[0] || null,
    credits: { earned, required, met: required > 0 && earned >= required },
    hours: { value: num(metrics.trainingHours), yearPct: (now - yearStart) / (365 * DAY) * 100 },
    certCounts: { total: certs.length, active: certs.length - expired, expired },
    certs,
    timeline: {
      months: Array.from({ length: 13 }, (_, i) => MONTH_NAMES[i % 12]),
      nowPct: Math.max(0, Math.min(100, (now - yearStart) / (rangeEnd - yearStart) * 100)),
      pos: (d) => Math.max(0, Math.min(100, (d - yearStart) / (rangeEnd - yearStart) * 100)),
      label: `Validity through ${MONTH_NAMES[0]} ${now.getFullYear() + 1}`,
    },
    attention,
    upcoming,
    track: showTrack ? { label: dashboard.trackLabel || "My training", pct: Math.max(0, Math.min(100, num(metrics.requiredProgress))), next: upcoming[0] ? `Next: ${upcoming[0].title}, ${upcoming[0].month} ${upcoming[0].day}` : "No upcoming sessions scheduled." } : null,
    navigate,
  };
}
